/**
 * Encryption of each kind of note content. Every ciphertext is bound (AES-GCM associated data)
 * to its scope, purpose and object id, so the server cannot swap blobs between notes or fields.
 */
import { aad, decryptBytes, decryptText, encryptBytes, encryptText, fromBase64, toBase64 } from "@brainlog/shared";
import { getScopeKey } from "./keyring";

export class LockedError extends Error {
  constructor() {
    super("Unlock this note or project first.");
  }
}

export function requireKey(scopeId: string) {
  const k = getScopeKey(scopeId);
  if (!k) throw new LockedError();
  return k;
}

export const encTitle = (scopeId: string, noteId: string, title: string) => encryptText(requireKey(scopeId).key, title, aad(scopeId, "title", noteId));
export const decTitle = (scopeId: string, noteId: string, blob: string) => decryptText(requireKey(scopeId).key, blob, aad(scopeId, "title", noteId));

export const encUpdate = async (scopeId: string, noteId: string, update: Uint8Array) =>
  toBase64(await encryptBytes(requireKey(scopeId).key, update, aad(scopeId, "update", noteId)));
export const decUpdate = (scopeId: string, noteId: string, blob: string) => decryptBytes(requireKey(scopeId).key, fromBase64(blob), aad(scopeId, "update", noteId));

export const encSnapshot = async (scopeId: string, noteId: string, state: Uint8Array) =>
  toBase64(await encryptBytes(requireKey(scopeId).key, state, aad(scopeId, "snapshot", noteId)));
export const decSnapshot = (scopeId: string, noteId: string, blob: string) => decryptBytes(requireKey(scopeId).key, fromBase64(blob), aad(scopeId, "snapshot", noteId));

// Versions and comments are bound to their note too, so they can't be moved to another note.
const child = (noteId: string, id: string) => `${noteId}/${id}`;

/** Versions hold the title and text of the note at that time. */
export const encVersion = (scopeId: string, noteId: string, versionId: string, v: { title: string; text: string }) =>
  encryptText(requireKey(scopeId).key, JSON.stringify({ t: v.title, md: v.text }), aad(scopeId, "version", child(noteId, versionId)));
export async function decVersion(scopeId: string, noteId: string, versionId: string, blob: string) {
  const o = JSON.parse(await decryptText(requireKey(scopeId).key, blob, aad(scopeId, "version", child(noteId, versionId))));
  return { title: String(o.t ?? ""), text: String(o.md ?? "") };
}

export const encComment = (scopeId: string, noteId: string, commentId: string, body: string) =>
  encryptText(requireKey(scopeId).key, body, aad(scopeId, "comment", child(noteId, commentId)));
export const decComment = (scopeId: string, noteId: string, commentId: string, blob: string) =>
  decryptText(requireKey(scopeId).key, blob, aad(scopeId, "comment", child(noteId, commentId)));
export const encAnchor = (scopeId: string, noteId: string, commentId: string, quote: string) =>
  encryptText(requireKey(scopeId).key, quote, aad(scopeId, "comment-anchor", child(noteId, commentId)));
export const decAnchor = (scopeId: string, noteId: string, commentId: string, blob: string) =>
  decryptText(requireKey(scopeId).key, blob, aad(scopeId, "comment-anchor", child(noteId, commentId)));

/** One-way proof of holding a scope's key; sent with every write of encrypted content. */
export const proofFor = (scopeId: string) => requireKey(scopeId).proof;

// Files ----------------------------------------------------------------------------
export interface FileMeta {
  filename: string;
  mime: string;
  size: number;
}

interface Envelope {
  v: 1;
  sid: string;
  fid: string;
  meta: string;
}

export function parseEnvelope(encMeta: string | null | undefined): Envelope | null {
  if (!encMeta) return null;
  try {
    const e = JSON.parse(encMeta);
    return e?.v === 1 && e.sid && e.fid && e.meta ? e : null;
  } catch {
    return null;
  }
}

export const scopeOfFile = (encMeta: string | null | undefined) => parseEnvelope(encMeta)?.sid ?? null;

/** Encrypt a file and its metadata (name, type, size) for upload. */
export async function encryptFile(scopeId: string, data: Uint8Array, meta: FileMeta) {
  const { key } = requireKey(scopeId);
  const fid = crypto.randomUUID();
  const ct = await encryptBytes(key, data, aad(scopeId, "file", fid));
  const encMeta = JSON.stringify({ v: 1, sid: scopeId, fid, meta: await encryptText(key, JSON.stringify(meta), aad(scopeId, "file-meta", fid)) } satisfies Envelope);
  return { blob: new Blob([ct as BlobPart], { type: "application/octet-stream" }), bytes: ct, encMeta };
}

export async function decryptFileMeta(encMeta: string): Promise<FileMeta> {
  const env = parseEnvelope(encMeta);
  if (!env) throw new Error("This file's encryption data is damaged.");
  const o = JSON.parse(await decryptText(requireKey(env.sid).key, env.meta, aad(env.sid, "file-meta", env.fid)));
  return { filename: String(o.filename ?? "file"), mime: String(o.mime ?? "application/octet-stream"), size: Number(o.size ?? 0) };
}

export async function decryptFileBytes(encMeta: string, ciphertext: Uint8Array) {
  const env = parseEnvelope(encMeta);
  if (!env) throw new Error("This file's encryption data is damaged.");
  return decryptBytes(requireKey(env.sid).key, ciphertext, aad(env.sid, "file", env.fid));
}

/** Download and decrypt an encrypted attachment. */
export async function fetchDecryptedFile(att: { id: string; encMeta: string | null }) {
  if (!att.encMeta) throw new Error("This file is not encrypted.");
  const res = await fetch(`/api/attachments/${att.id}/file?download=1`, { credentials: "same-origin" });
  if (!res.ok) throw new Error("The file could not be downloaded. Please try again.");
  const plain = await decryptFileBytes(att.encMeta, new Uint8Array(await res.arrayBuffer()));
  const meta = await decryptFileMeta(att.encMeta);
  return { meta, bytes: plain, blob: new Blob([plain as BlobPart], { type: meta.mime }) };
}

export async function sha256Hex(text: string) {
  const d = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
